import { Box } from "@mui/material";
import { DotLegalAlert, DotLegalButton, DotLegalDialog, DotLegalHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import DotLegalLink from "@common/components/dotLegalLink/DotLegalLink";
import LinkedControlsBox from "../../../policy/policySections/LinkedControlsBox";
import LinkedControlsSection from "../../riskControls/LinkedControlsSection";
import { useMitigationPlanDialog } from "./MitigationPlanDialog.hooks";
import MitigationPlanFormFields from "./MitigationPlanFormFields";
import { TreatmentDialogProps } from "./TreatmentDialogs.types";
import { useTreatmentDialogStyles } from "./TreatmentDialogs.styles";

function MitigationPlanDialog(props: TreatmentDialogProps) {
    const { translateString } = useTranslation();
    const styles = useTreatmentDialogStyles();
    const {
        planForm,
        isLoading,
        isReadOnly,
        isDrifted,
        driftWarningText,
        reconfirm,
        showStaleMitigationPrompt,
        staleWarningText,
        createNewMitigation,
        inProgress,
        onSave,
        linkedTaskUrl,
        currentRisk,
        consequenceOptions,
        probabilityOptions,
        currentExposureText,
        availableControls,
        existingCoverageControls,
    } = useMitigationPlanDialog(props);

    const linkedTaskSlot = linkedTaskUrl ? (
        <Box sx={styles.linkedTask}>
            <DotLegalLink to={linkedTaskUrl} linkColor="primary">
                {translateString("openTask")}
            </DotLegalLink>
        </Box>
    ) : undefined;

    const currentRiskSubtitle = currentRisk ? (
        <DotLegalHeader color="lightgrey" headerStyle="extraSmall" marginBottom={2} marginTop={0} fontWeight={400}>
            {translateString("current")}: {translateString("consequence")}{" "}
            <Box component="span" sx={styles.scoreValue}>
                {currentRisk.consequence} - {currentRisk.consequenceLabel}
            </Box>{" "}
            | {translateString("probability")}{" "}
            <Box component="span" sx={styles.scoreValue}>
                {currentRisk.probability} - {currentRisk.probabilityLabel}
            </Box>
            {currentExposureText && (
                <>
                    {" "}
                    | {translateString("riskExposureSettingsHeader")}{" "}
                    <Box component="span" sx={styles.scoreValue}>
                        {currentExposureText}
                    </Box>
                </>
            )}
        </DotLegalHeader>
    ) : undefined;

    return (
        <DotLegalDialog
            open
            header={translateString("mitigate")}
            buttonOkText={translateString("save")}
            hideOkButton={isReadOnly}
            onDialogClose={props.onClose}
            onOkClick={onSave}
            inProgress={inProgress}
            size={"md"}
            isLoading={isLoading}
        >
            <Box sx={styles.content}>
                <Box sx={styles.subHeader}>
                    <DotLegalHeader center marginTop={-1} marginBottom={0} fontWeight={400} headerStyle="small">
                        {translateString("riskTreatmentMitigateDescription")}
                    </DotLegalHeader>
                </Box>
                {isDrifted && !isReadOnly && (
                    <DotLegalAlert
                        severity={"info"}
                        warningText={driftWarningText}
                        action={
                            <DotLegalButton buttonType="secondary" size="small" onClick={reconfirm}>
                                {translateString("reconfirm")}
                            </DotLegalButton>
                        }
                    />
                )}

                {showStaleMitigationPrompt && (
                    <DotLegalAlert
                        severity={"warning"}
                        warningText={staleWarningText}
                        action={
                            <DotLegalButton buttonType="secondary" size="small" onClick={createNewMitigation}>
                                {translateString("createNewMitigation")}
                            </DotLegalButton>
                        }
                    />
                )}

                <MitigationPlanFormFields
                    fields={planForm}
                    consequenceOptions={consequenceOptions}
                    probabilityOptions={probabilityOptions}
                    isReadOnly={isReadOnly}
                    afterDeadlineSlot={linkedTaskSlot}
                    expectedRiskSubtitle={currentRiskSubtitle}
                />

                {existingCoverageControls.length > 0 && <LinkedControlsBox controls={existingCoverageControls} heading="existingControls" />}

                {planForm.showLinkedControls && (
                    <LinkedControlsSection
                        linkedControlIds={planForm.frameworkControlIds}
                        availableControls={availableControls}
                        onChange={planForm.setFrameworkControlIds}
                        disabled={isReadOnly}
                        heading="controlsAddedByThisPlan"
                    />
                )}
            </Box>
        </DotLegalDialog>
    );
}

export default MitigationPlanDialog;
